import { BASE_URL, SITE_NAME, SITE_DESCRIPTION, SITE_SOCIAL_IMAGE, SITE_THEME_COLOR } from '@/config/site'
import { useRouter, useHead, useSeoMeta, useRuntimeConfig } from '#imports'
import { removeTrailingSlash } from '~/utils/routing'

const useSeoHead = (meta) => {
	const router = useRouter()
	const path = router.currentRoute.value.path
	const url = `${removeTrailingSlash(BASE_URL)}${path}`
	const runtimeConfig = useRuntimeConfig()

	function parseTitle() {
		let title

		if (router.currentRoute.value.path === '/') {
			title = meta.title + ' — ' + SITE_DESCRIPTION
		} else {
			title = meta.title + ' | ' + meta.sitename + ' — ' + meta.sitedescription
		}

		return title
	}

	let link = [
		{
			rel: 'canonical',
			href: url,
		},
		{ rel: 'icon', type: 'image/png', href: '/favicon-96x96.png', sizes: '96x96' },
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
		{ rel: 'shortcut icon', href: '/favicon.ico' },
		{ rel: 'apple-touch-icon', type: 'image/png', href: '/apple-touch-icon.png', sizes: '180x180' },
		{ rel: 'manifest', href: '/site.webmanifest' },
	]

	// https://developers.google.com/search/docs/specialty/international/localized-versions?hl=en&visit_id=638549870917371248-3960734594&rd=1
	if (Object.hasOwn(meta, 'alternates') && meta.alternates?.length) {
		const alternates = meta.alternates.map(({ lang, path }) => ({
			ref: 'alternate',
			hreflang: lang,
			href: `${removeTrailingSlash(BASE_URL)}${path}`,
		}))

		link = [...link, ...alternates]
	}

	const htmlAttrs = {
		lang: meta.lang || 'en',
	}

	useHead(
		{
			script: meta?.head?.scripts ? [...meta.head.scripts] : [],
		},
		{ mode: 'client' }
	)

	// ******************** Inject scripts downloaded from Sanity //
	const scripts = runtimeConfig.public?.scripts || []
	if (scripts.length) {
		useHead(
			{
				script: scripts.map((src) => ({
					src,
					async: true,
					defer: true,
				})),
			},
			{ mode: 'client' }
		)
	}
	// ******************** //

	useHead({
		viewport: 'width=device-width, initial-scale=1',
		charset: 'utf-8',
		title: parseTitle(),
		meta: [{ name: 'theme-color', content: SITE_THEME_COLOR }, { name: 'viewport', content: 'width=device-width, initial-scale=1.0, interactive-widget=resizes-content' }, ...meta.head?.tags],
		link: link,
		htmlAttrs: htmlAttrs,
	})

	// Build SEO meta config
	const ogImageUrl = meta.image?.url

	useSeoMeta({
		charset: 'utf-8',
		formatDetection: 'telephone=no',
		description: meta.description || SITE_DESCRIPTION,
		keywords: meta.keywords ? meta.keywords : '',
		author: meta.sitename || SITE_NAME,
		ogTitle: parseTitle(),
		ogDescription: meta.description || SITE_DESCRIPTION,
		ogType: 'website',
		ogSiteName: meta.sitename || SITE_NAME,
		ogUrl: url,
		ogImage: ogImageUrl,
		ogImageWidth: meta.image?.width || '1400',
		ogImageHeight: meta.image?.height || '700',
		twitterTitle: parseTitle(),
		twitterDescription: meta.description || SITE_DESCRIPTION,
		twitterCard: 'summary_large_image',
		twitterImage: ogImageUrl,
	})
}
export { useSeoHead }
